/**
 * The file side of a policy: read the document, upload the next version,
 * download the current PDF, reach any earlier version, and re-run its
 * processing. View document opens the PDF in a preview over the page (the
 * page owns it, so a `#document` link opens the same one). The rest is a split
 * button — Upload new version is the primary action for anyone
 * who can manage the library, and the chevron opens the downloads and
 * reprocessing — and the Versions tab reuses the upload dialog so there is one
 * way to add a version, not two that drift. Members who cannot upload get
 * Download as the primary instead.
 *
 * Reprocess re-reads the current file (same job rows as an upload, no new
 * version): the way back from a failed run without uploading again.
 *
 * Save as template copies the current file into the firm's own templates, so
 * eve can start the next entity's or the next year's policy from it.
 *
 * A new version never overwrites: the previous file stays in the policy's
 * history, because "what was in force last quarter" is exactly the question
 * an inspection asks.
 *
 * @module components/regulation/policy/PolicyFileActions
 */
import {
  ChevronDownIcon,
  DownloadIcon,
  EyeIcon,
  FileUpIcon,
  HistoryIcon,
  LayoutTemplateIcon,
  RefreshCwIcon,
} from "lucide-react";
import { useRef, useState } from "react";

import { cn } from "~/lib/utils";

import { useOrgProfile } from "../../../state/orgProfile";
import {
  policyDisplayName,
  policyFileVersions,
  policyVersion,
  usePolicies,
  type Policy,
} from "../../../state/policies";
import { usePolicyTemplates } from "../../../state/policyTemplates";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../../ui/dialog";
import { Group, GroupSeparator } from "../../ui/group";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../../ui/menu";
import { Textarea } from "../../ui/textarea";
import { toastManager } from "../../ui/toast";


/**
 * Pick a PDF, say what changed, upload. Controlled from outside so the header
 * menu and the Versions tab can both open it.
 */
const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export function PolicyVersionUploadDialog({
  organizationId,
  policy,
  open,
  onOpenChange,
}: {
  readonly organizationId: string | null;
  readonly policy: Policy;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const { replaceFile } = usePolicies(organizationId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nextVersion = policyVersion(policy) + 1;

  const reset = () => {
    setFile(null);
    setNote("");
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    if (file === null) {
      setError("Choose the PDF for this version.");
      return;
    }
    setBusy(true);
    setError(null);
    const trimmed = note.trim();
    const message = await replaceFile(policy.id, file, trimmed.length === 0 ? null : trimmed);
    setBusy(false);
    if (message !== null) {
      setError(message);
      return;
    }
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogPopup className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload version {nextVersion}</DialogTitle>
          <DialogDescription>
            The current file (v{policyVersion(policy)}) stays in the version history. The new one
            is re-read so answers and mappings follow it.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setError(null);
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-dashed border-border px-4 py-4 text-left transition-colors hover:border-foreground/30 hover:bg-muted/40"
          >
            <FileUpIcon className="size-5 shrink-0 text-muted-foreground" />
            {file === null ? (
              <span className="min-w-0">
                <span className="block text-sm font-medium text-foreground">Choose a PDF</span>
                <span className="block text-xs text-muted-foreground">
                  Replaces {policy.filename}
                </span>
              </span>
            ) : (
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">
                  {file.name}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {formatBytes(file.size)} · click to change
                </span>
              </span>
            )}
          </button>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">What changed</span>
            <Textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="e.g. Annual review — updated the PEP screening thresholds"
              rows={3}
              className="resize-none"
            />
          </label>
          {error !== null && <p className="text-xs text-destructive">{error}</p>}
        </DialogPanel>
        <DialogFooter>
          <DialogClose
            render={
              <Button type="button" size="sm" variant="ghost" className="cursor-pointer">
                Cancel
              </Button>
            }
          />
          <Button
            type="button"
            size="sm"
            onClick={() => void submit()}
            disabled={busy || file === null}
            className="cursor-pointer gap-1.5"
          >
            <FileUpIcon className="size-3.5" />
            {busy ? "Uploading…" : `Upload v${nextVersion}`}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

/** What the Reprocess control shows: idle, the request in flight, or the run it started. */
export function reprocessLabel(input: {
  readonly status: Policy["status"];
  readonly requesting: boolean;
}): { readonly label: string; readonly disabled: boolean } {
  if (input.requesting) return { label: "Restarting…", disabled: true };
  if (input.status === "processing") return { label: "Processing…", disabled: true };
  return { label: "Reprocess", disabled: false };
}

/**
 * A header action's label: shown in a wide header, read out only (the icon
 * stays) in a narrow one — the `page-header` container of
 * `RegulationPageShell` — so the title keeps the width.
 */
const HEADER_LABEL = "sr-only @[52rem]/page-header:not-sr-only";
/** ...and the button around it closes up into a square. */
const HEADER_BUTTON = "@max-[52rem]/page-header:aspect-square @max-[52rem]/page-header:px-0";

/**
 * The header's file actions: View document, then the split button — Upload new
 * version, plus a menu for downloads and reprocessing.
 */
export function PolicyFileActions({
  organizationId,
  policy,
  onViewDocument,
}: {
  readonly organizationId: string | null;
  readonly policy: Policy;
  readonly onViewDocument: () => void;
}) {
  const { download, reprocess } = usePolicies(organizationId);
  const { saveFromPolicy } = usePolicyTemplates(organizationId);
  const { canManage } = useOrgProfile(organizationId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const history = policyFileVersions(policy);
  const current = policyVersion(policy);
  const reprocessState = reprocessLabel({ status: policy.status, requesting: reprocessing });

  const runReprocess = async () => {
    if (reprocessState.disabled) return;
    setReprocessing(true);
    const message = await reprocess(policy.id);
    setReprocessing(false);
    if (message === null) {
      toastManager.add({
        type: "success",
        title: "Processing restarted",
        description: "The current file is being re-read and re-indexed. Progress shows on the Overview tab.",
      });
    } else {
      toastManager.add({ type: "error", title: "Processing not restarted", description: message });
    }
  };

  const saveAsTemplate = async () => {
    if (savingTemplate) return;
    setSavingTemplate(true);
    const outcome = await saveFromPolicy({ policyId: policy.id, title: policyDisplayName(policy) });
    setSavingTemplate(false);
    if (outcome.error === null) {
      toastManager.add({
        type: "success",
        title: "Saved as a template",
        description: "It is on the Templates page. Later changes to this policy do not change the template.",
      });
    } else {
      toastManager.add({ type: "error", title: "Not saved as a template", description: outcome.error });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={cn("cursor-pointer gap-1.5", HEADER_BUTTON)}
        onClick={onViewDocument}
      >
        <EyeIcon className="size-3.5" />
        <span className={HEADER_LABEL}>View document</span>
      </Button>
      {/* The workspace's split button: primary action left, menu right, one group. */}
      <Group aria-label="Policy file">
        {canManage ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={cn("cursor-pointer gap-1.5", HEADER_BUTTON)}
            onClick={() => setUploadOpen(true)}
          >
            <FileUpIcon className="size-3.5" />
            <span className={HEADER_LABEL}>Upload new version</span>
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={cn("cursor-pointer gap-1.5", HEADER_BUTTON)}
            onClick={() => download(policy.id)}
          >
            <DownloadIcon className="size-3.5" />
            <span className={HEADER_LABEL}>Download</span>
          </Button>
        )}
        <GroupSeparator />
        <Menu>
          <MenuTrigger
            render={
              <Button
                type="button"
                size="icon-sm"
                variant="outline"
                aria-label="More file actions"
                className="cursor-pointer"
              />
            }
          >
            <ChevronDownIcon aria-hidden="true" className="size-4" />
          </MenuTrigger>
          <MenuPopup align="end">
            <MenuItem onClick={() => download(policy.id)}>
              <DownloadIcon className="size-4" />
              Download v{current}
              <span className="ml-auto text-xs text-muted-foreground">current</span>
            </MenuItem>
            {history.length > 0 && <MenuSeparator />}
            {[...history].reverse().map((version) => (
              <MenuItem key={version.version} onClick={() => download(policy.id, version.version)}>
                <HistoryIcon className="size-4" />
                Download v{version.version}
              </MenuItem>
            ))}
            {canManage && (
              <>
                <MenuSeparator />
                <MenuItem disabled={reprocessState.disabled} onClick={() => void runReprocess()}>
                  <RefreshCwIcon className="size-4" />
                  {reprocessState.label}
                </MenuItem>
                <MenuItem disabled={savingTemplate} onClick={() => void saveAsTemplate()}>
                  <LayoutTemplateIcon className="size-4" />
                  {savingTemplate ? "Saving as template…" : "Save as template"}
                </MenuItem>
              </>
            )}
          </MenuPopup>
        </Menu>
      </Group>
      {canManage && (
        <PolicyVersionUploadDialog
          organizationId={organizationId}
          policy={policy}
          open={uploadOpen}
          onOpenChange={setUploadOpen}
        />
      )}
    </div>
  );
}
